import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { StyledNavigationBar, StyledNavigationBarContainer, StyledIconButton, StyledListItem } from './index.styles.js';
import {} from '../icon/index.js';
import { useShowScrollButtons } from './use-show-scroll-buttons.js';
import {} from '@oneaudi/unified-web-alpha-common';
import { pickA11yProps } from '../../utils/pick-a11y-props.js';
import { pickDataProps } from '../../utils/pick-data-props.js';
import { useArrowKeyTabs } from '../../hooks/use-arrow-navigation.js';
import { handleScrollInView } from '../../utils/handle-scroll-in-view.js';
import { NavigationBarUnit } from './navigation-bar-unit/index.js';
const NavigationBarItem = forwardRef(({ item, showScrollButtons, size, containerRef }, ref) => {
    const internalRef = useRef(null);
    useImperativeHandle(ref, () => internalRef.current);
    useEffect(() => {
        if (!showScrollButtons)
            return;
        if (item.active)
            handleScrollInView({ containerRef, itemRef: internalRef });
    }, [item.active, showScrollButtons, containerRef]);
    useEffect(() => {
        const container = containerRef.current;
        if (item.active && container?.contains(document.activeElement)) {
            internalRef.current?.focus();
        }
    }, [item.active, containerRef]);
    const leadingIcon = item.iconPosition === 'leading' && item.icon ? item.icon : undefined;
    const trailingIcon = item.iconPosition === 'trailing' && item.icon ? item.icon : undefined;
    return (React.createElement(StyledListItem, { role: "none" }, item.href ? (React.createElement(NavigationBarUnit, { leadingIcon: leadingIcon, trailingIcon: trailingIcon, active: item.active, "aria-current": item.active ? 'page' : undefined, size: size, role: "menuitem", href: item.href, onClick: item.onClick, target: item.target, ref: internalRef, rel: item.rel, "aria-label": item['aria-label'], tabIndex: item.active ? 0 : -1 }, item.label)) : (React.createElement(NavigationBarUnit, { leadingIcon: leadingIcon, trailingIcon: trailingIcon, active: item.active, "aria-current": item.active ? 'page' : undefined, size: size, role: "menuitem", onClick: item.onClick, disabled: item.disabled, ref: internalRef, "aria-label": item['aria-label'], tabIndex: item.active ? 0 : -1 }, item.label))));
});
NavigationBarItem.displayName = 'NavigationBarItem';
export const NavigationBar = React.forwardRef(({ items, size, id, defaultScrollable, scrollButtons, onListElementKeyDown, className, onKeyNavigate, margin, ...rest }, ref) => {
    const a11yProps = pickA11yProps(rest);
    const dataProps = pickDataProps(rest);
    const navigationBarRef = useRef(null);
    const activeIndex = items.findIndex((item) => item.active);
    const { isScrollable, inactiveArrow } = useShowScrollButtons(navigationBarRef);
    const showScrollButtons = defaultScrollable || isScrollable;
    const handleScroll = useCallback((direction, event) => {
        const element = navigationBarRef.current;
        if (!element) {
            return;
        }
        const multiplier = 0.75;
        const scrollLength = element.offsetWidth * multiplier;
        element.scrollBy({
            left: direction === 'left' ? -scrollLength : scrollLength,
            behavior: 'smooth'
        });
        scrollButtons[direction]?.onClick?.(event);
    }, []);
    useArrowKeyTabs(navigationBarRef, items, activeIndex, onKeyNavigate);
    return (React.createElement(StyledNavigationBarContainer, { ...a11yProps, ...dataProps, className: className, ref: ref, margin: margin },
        showScrollButtons && (React.createElement(StyledIconButton, { variant: "tertiary", icon: "back", size: "sm", "aria-label": scrollButtons.left.ariaLabel, onClick: (e) => handleScroll('left', e), disabled: inactiveArrow === 'left' })),
        React.createElement(StyledNavigationBar, { role: "menubar", ref: navigationBarRef, onKeyDown: onListElementKeyDown }, items.map((item, i) => {
            return (React.createElement(NavigationBarItem, { key: `${id}-${i}`, containerRef: navigationBarRef, size: size, item: item, showScrollButtons: showScrollButtons, ref: item.ref }));
        })),
        showScrollButtons && (React.createElement(StyledIconButton, { variant: "tertiary", icon: "forward", size: "sm", "aria-label": scrollButtons.right.ariaLabel, onClick: (e) => handleScroll('right', e), disabled: inactiveArrow === 'right' }))));
});
NavigationBar.displayName = 'NavigationBar';
//# sourceMappingURL=index.js.map